import { toTitleCase } from '@dealerportal/utilities';
import { floorlyticsBlob } from '../../utils/analytics';
// @ts-ignore - Dynamic dependency resolved at runtime
import { analyticsReady, domReady } from '../../utils/ready';
import type { TrafficInfo } from './types';

/**
 * The mapping of these source values is determined on an analysis of the
 * values we have received recently. Please note that certain mapping were
 * skipped (such as yellowpages) since they are so rare in practice.
 *
 * {@link https://gist.github.com/ianks/c899971eb95de5bc8d93fa51b0908b25#file-current-lead-source-analysis-md Cukimonsta Analysis}
 */
function determineSourceVal(trafficInfo: TrafficInfo): string {
  if (trafficInfo.medium === 'cpc' && trafficInfo.source === 'google') {
    return 'Google PPC';
    // biome-ignore lint/style/noUselessElse: <explanation>
  } else if (trafficInfo.type === 'organic') {
    return 'Organic Search';
    // biome-ignore lint/style/noUselessElse: <explanation>
  } else if (trafficInfo.medium && trafficInfo.medium !== 'none') {
    return toTitleCase(trafficInfo.medium);
    // biome-ignore lint/style/noUselessElse: <explanation>
  } else {
    return toTitleCase(trafficInfo.type || '');
  }
}

/**
 * Maps sourcedetail values, which is just a compatible one-to-one mapping of
 * sourcebuster's determined source
 *
 * {@link https://gist.github.com/ianks/c899971eb95de5bc8d93fa51b0908b25#file-current-lead-source-analysis-md Cukimonsta Analysis}
 */
function determineSourceDetailVal(trafficInfo: TrafficInfo) {
  return trafficInfo.source;
}

/** Matches every input that receives traffic attribution. */
export const TRAFFIC_INPUT_SELECTOR =
  'input[name^="traffic-"], input[name^="utm_"], input[name="source"], input[name="sourcedetail"]';

/**
 * Set a value on a bunch of inputs at one
 */
function setValueOnAllInputs(sel: string, val: string, attempts = 0) {
  const inputs = document.querySelectorAll<HTMLInputElement>(sel);

  // Try up to 10 times to load find form
  if (attempts < 10 && inputs.length === 0) {
    setTimeout(() => setValueOnAllInputs(sel, val, attempts + 1), 1000);
  } else {
    for (const input of inputs) {
      input.value = val;
    }
  }
}

/**
 * Sets the traffic information on every matching input in the document now,
 * without retrying. For inputs that render after the initial attachment.
 */
export function fillTrafficInputs(trafficInfo: TrafficInfo): void {
  const values: [selector: string, value: string][] = [
    ['input[name="source"]', determineSourceVal(trafficInfo)],
    ['input[name="sourcedetail"]', determineSourceDetailVal(trafficInfo)],
  ];
  for (const key in trafficInfo) {
    const value = trafficInfo[key as keyof TrafficInfo];
    values.push([`input[name="traffic-${key}"]`, value]);
    values.push([`input[name="utm_${key}"]`, value]);
  }

  for (const [sel, val] of values) {
    for (const input of document.querySelectorAll<HTMLInputElement>(sel)) {
      input.value = val;
    }
  }
}

/**
 * Sets the traffic information for all Act-On form inputs on a page in a way
 * that is compatible with Cukimonsta.js
 *
 * {@link https://gist.github.com/ianks/ed96310fcc07ab146fdda063cbe97f98 Cukimonsta Information}
 */
export async function attachToFF8Form(trafficInfo: TrafficInfo): Promise<void> {
  await domReady();

  const mappedSourceVal = determineSourceVal(trafficInfo);
  const mappedSourceDetailVal = determineSourceDetailVal(trafficInfo);

  setValueOnAllInputs('input[name="source"]', mappedSourceVal);
  setValueOnAllInputs('input[name="sourcedetail"]', mappedSourceDetailVal);
}

async function syncBlobToInput(eventName: string) {
  await analyticsReady();

  window.analytics!.on?.(eventName, () => {
    setTimeout(
      async () =>
        setValueOnAllInputs(
          'input[name="floorlytics-blob"]',
          await floorlyticsBlob()
        ),
      10
    );
  });
}
/**
 * Sets the traffic information for all forms which have inputs names traffic-*
 *
 * {@link https://gist.github.com/ianks/ed96310fcc07ab146fdda063cbe97f98 Cukimonstao Infomation}
 */
export async function attachToAllCompatibleForms(
  trafficInfo: TrafficInfo
): Promise<void> {
  await domReady();

  // An improved way of passing along traffic information, once we add these fields
  for (const key in trafficInfo) {
    setValueOnAllInputs(
      `input[name="traffic-${key}"]`,
      (trafficInfo as any)[key]
    );
    setValueOnAllInputs(`input[name="utm_${key}"]`, (trafficInfo as any)[key]);
  }

  ['page', 'identify', 'alias', 'group'].forEach((eventName) => {
    syncBlobToInput(eventName);
  });

  setValueOnAllInputs(
    'input[name="floorlytics-blob"]',
    await floorlyticsBlob()
  );
}
